import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { logger } from '@/utils/logger';
import type { Project } from '@fmeastudio/shared'
import { getProjects, ProjectsFetchError } from '../api/projects'
import { ProjectLoadError } from './ProjectLoadError'

interface ProjectContextType {
  activeProject: Project | null
  currentProject: Project | null // Alias for activeProject
  setActiveProject: (project: Project | null) => void
  isLoading: boolean
  projects: Project[] // All projects in the system
  /** Set once the bounded auto-retry has been exhausted; null while loading/healthy. */
  error: string | null
  /** Manually re-run the load from scratch (resets the retry budget). Used by the error UI's "Retry" action. */
  retry: () => void
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

const STORAGE_KEY = 'activeProjectId'

// Bounded retry: a transient blip (429, brief network hiccup) should
// self-recover without the user ever seeing an error. Only after
// MAX_AUTO_RETRIES does this become a real, user-facing error state.
const MAX_AUTO_RETRIES = 3
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 10000

function delayForAttempt(attempt: number, retryAfterSeconds?: number): number {
  if (typeof retryAfterSeconds === 'number' && retryAfterSeconds >= 0) {
    // Server told us exactly how long to wait (429) — honor it over backoff.
    return Math.min(retryAfterSeconds * 1000, MAX_DELAY_MS * 3)
  }
  return Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [activeProject, setActiveProjectState] = useState<Project | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Guards against React 18 StrictMode's dev-only double-invoke of effects,
  // and against overlapping loads if retry() is clicked while a load is
  // already in flight. Refs (unlike state) survive the double-invoke without
  // triggering a re-render, so the second invocation sees `true` and skips.
  const hasStartedRef = useRef(false)
  const loadInFlightRef = useRef(false)
  // Bumped on every retry() so an in-flight retried load can tell it's been
  // superseded and should not clobber state set by a newer load.
  const loadGenerationRef = useRef(0)

  const loadActiveProject = useCallback(async () => {
    if (loadInFlightRef.current) return
    loadInFlightRef.current = true
    const generation = ++loadGenerationRef.current

    setIsLoading(true)
    setError(null)

    let attempt = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const storedProjectId = localStorage.getItem(STORAGE_KEY)
        const fetchedProjects = await getProjects()

        if (generation !== loadGenerationRef.current) return // superseded by a newer retry()

        setProjects(fetchedProjects)

        if (fetchedProjects.length === 0) {
          setActiveProjectState(null)
        } else {
          const storedProject = storedProjectId
            ? fetchedProjects.find((p) => p.id === storedProjectId)
            : undefined
          const nextActive = storedProject ?? fetchedProjects[0]
          setActiveProjectState(nextActive ?? null)
          if (nextActive) {
            localStorage.setItem(STORAGE_KEY, nextActive.id)
          }
        }

        setError(null)
        setIsLoading(false)
        loadInFlightRef.current = false
        return
      } catch (err) {
        if (generation !== loadGenerationRef.current) return // superseded

        logger.error(`Error loading active project (attempt ${attempt})`, err, { module: 'ProjectContext' })

        if (attempt >= MAX_AUTO_RETRIES) {
          const message =
            err instanceof ProjectsFetchError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Failed to load projects.'
          setError(message)
          setIsLoading(false)
          loadInFlightRef.current = false
          return
        }

        const retryAfterSeconds = err instanceof ProjectsFetchError ? err.retryAfterSeconds : undefined
        const wait = delayForAttempt(attempt, retryAfterSeconds)
        attempt += 1
        await sleep(wait)
        // loop retries; isLoading stays true so the UI shows "loading", not "errored"
      }
    }
  }, [])

  const retry = useCallback(() => {
    loadInFlightRef.current = false // allow a fresh load even if one is (stuck) in flight
    void loadActiveProject()
  }, [loadActiveProject])

  useEffect(() => {
    if (hasStartedRef.current) return
    hasStartedRef.current = true
    void loadActiveProject()
  }, [loadActiveProject])

  const setActiveProject = (project: Project | null) => {
    setActiveProjectState(project)
    if (project) {
      localStorage.setItem(STORAGE_KEY, project.id)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  return (
    <ProjectContext.Provider
      value={{
        activeProject,
        currentProject: activeProject,
        setActiveProject,
        isLoading,
        projects,
        error,
        retry,
      }}
    >
      {/* Only block the whole app when we have NO usable project list yet —
          a refresh failure after a successful initial load leaves the app
          usable with the last-known projects rather than hard-blocking it. */}
      {error && projects.length === 0 ? (
        <ProjectLoadError message={error} onRetry={retry} />
      ) : (
        children
      )}
    </ProjectContext.Provider>
  )
}

export function useProject() {
  const context = useContext(ProjectContext)
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider')
  }
  return context
}
